import { BaseView } from '@src/views/BaseView.tsx';
import { DirectusContextWrapper } from '@components/directusTable/directusContext.tsx';
import {
  Navigate,
  Outlet,
  type RouteObject,
  createBrowserRouter,
} from 'react-router-dom';
import { Paths } from '@src/paths.ts';
import { UserContextWrapper } from './contexts/userContext.tsx';
import { chatbotBuilderViewLoader } from './views/chatbotBuilder/ChatBotBuilderView.loader.ts';
import { chatbotViewLoader } from './views/chatbot/ChatBotView.loader.ts';
import { editJobWizardViewLoader } from '@src/views/createJobWizard/createJobWizardView.loader.ts';
import { jobsDetailsViewLoader } from '@src/views/jobDetails/JobsDetailsView.loader.ts';
import { loginLoader } from './views/login/LoginView.functions.ts';
import {
  logoutAction,
  logoutLoader,
} from './views/logout/LogoutView.functions.ts';
import { organizationDetailsViewLoader } from '@src/views/organizationDetails/OrganizationDetailsView.loader.ts';
import { organizationsListViewLoader } from '@src/views/organizations/OrganizationsListView.functions.ts';
import { profileViewLoader } from '@src/views/profile/ProfileView.loader.ts';
import { registerLoader } from '@src/views/register/RegisterView.loader.ts';
import { rootLoader } from '@src/views/root/Root.loader.ts';
import GlobalErrorHandler from './views/global-error-handler.tsx';
import JobWizardView from '@src/views/createJobWizard/JobWizardView.tsx';
import JobsView from '@src/views/jobs/JobsView.tsx';
import OrganizationDetailsView from '@src/views/organizationDetails/OrganizationDetailsView.tsx';
import OrganizationInviteAcceptView from '@src/views/organizations/OrganizationInviteAcceptView.tsx';
import OrganizationsListView from '@src/views/organizations/OrganizationsListView.tsx';
import Root from './views/root/Root.tsx';
import { GTMWrapper } from './contexts/GTMWrapper.tsx';
import { creativeBuilderForJobViewLoader } from './views/creativeBuilder/CreativeBuilderForJobView.loader.ts';

export const routes: RouteObject[] = [
  {
    errorElement: <GlobalErrorHandler />,
    children: [
      {
        id: 'app',
        children: [
          {
            path: Paths.LOGIN,
            lazy: () =>
              import('./views/login/LoginView.tsx').then((module) => ({
                element: <module.LoginView />,
                errorElement: <module.LoginView />,
              })),
            loader: loginLoader,
          },
          {
            path: Paths.LOGOUT,
            lazy: () => import('./views/logout/LogoutView.tsx'),
            loader: logoutLoader,
            action: logoutAction,
            errorElement: <GlobalErrorHandler />,
          },
          {
            path: Paths.REGISTER,
            loader: registerLoader,
            children: [
              {
                index: true,
                lazy: () => import('./views/register/RegisterView.tsx'),
              },
              {
                path: Paths.VERIFY_MAIL.slice(1),
                lazy: () => import('./views/register/VerifyEmailView.tsx'),
              },
            ],
          },
          {
            path: Paths.RESET,
            lazy: () => import('./views/profile/ResetView.tsx'),
            errorElement: <GlobalErrorHandler />,
          },
          {
            path: Paths.START_PASSWORD_RESET,
            lazy: () => import('./views/login/StartPasswordResetView.tsx'),
            errorElement: <GlobalErrorHandler />,
          },
          {
            id: 'root',
            element: (
              <Root>
                <DirectusContextWrapper>
                  <Outlet />
                </DirectusContextWrapper>
              </Root>
            ),
            loader: rootLoader,
            shouldRevalidate: (params) => {
              // we don't need to revalidate everytime.
              // DefaultShouldRevalidate is only true if we enter the website the first time,
              // or after we call revalidator.revalidate
              if (!params.defaultShouldRevalidate) {
                return false;
              }
              // entering the wizard is considered a new page call,
              // so we need to prevent the revalidation or the wizard breaks
              // We otherwise want to allow to manual revalidate in case the user switches his settings
              return (
                !params.currentUrl.pathname.includes('/jobs') &&
                !params.currentUrl.pathname.includes('/wizard')
              );
            },
            children: [
              {
                index: true,
                element: <Navigate to={Paths.JOBS} replace={true} />,
              },
              {
                element: (
                  <BaseView>
                    <Outlet />
                  </BaseView>
                ),
                children: [
                  {
                    path: Paths.ORGANIZATIONS,
                    children: [
                      {
                        index: true,
                        element: <OrganizationsListView />,
                        loader: organizationsListViewLoader,
                      },
                      {
                        path: ':organizationId/accept',
                        element: <OrganizationInviteAcceptView />,
                      },
                      {
                        path: ':organizationId',
                        element: <OrganizationDetailsView />,
                        loader: organizationDetailsViewLoader,
                      },
                    ],
                  },
                  {
                    path: Paths.JOBS,
                    children: [
                      {
                        index: true,
                        element: <JobsView />,
                      },
                      {
                        path: Paths.JOB_DETAILS,
                        lazy: () =>
                          import('./views/jobDetails/JobDetailsView.tsx'),
                        loader: jobsDetailsViewLoader,
                      },
                      {
                        path: Paths.JOB_CREATIVE_BUILDER,
                        lazy: () =>
                          import(
                            './views/creativeBuilder/CreativeBuilderForJobView.tsx'
                          ),
                        loader: creativeBuilderForJobViewLoader,
                      },
                    ],
                  },
                  {
                    path: Paths.PROFILE,
                    lazy: () => import('./views/profile/ProfileView.tsx'),
                    loader: profileViewLoader,
                  },
                  {
                    path: Paths.SUPPORT,
                    lazy: () => import('./views/support/SupportView.tsx'),
                  },
                  {
                    path: Paths.COACHING,
                    lazy: () => import('./views/support/CoachingView.tsx'),
                  },
                  {
                    path: '/creativeBuilder',
                    lazy: () =>
                      import('./views/creativeBuilder/CreativeBuilderView.tsx'),
                  },
                ],
              },
              // Routes without BaseView
              {
                path: Paths.JOB_WIZARD,
                element: (
                  <UserContextWrapper>
                    <JobWizardView />
                  </UserContextWrapper>
                ),
                loader: editJobWizardViewLoader,
                shouldRevalidate: ({ currentParams, nextParams }) => {
                  return currentParams.jobId !== nextParams.jobId;
                },
              },
              {
                path: Paths.CHATBOT_BUILDER,
                lazy: () =>
                  import('./views/chatbotBuilder/ChatbotBuilderView.tsx'),
                loader: chatbotBuilderViewLoader,
              },
            ],
          },
        ],
        element: (
          <GTMWrapper>
            <Outlet />
          </GTMWrapper>
        ),
      },
      {
        id: 'chatbot-view',
        path: Paths.CHATBOT,
        errorElement: <GlobalErrorHandler />,
        lazy: () => import('./views/chatbot/ChatBotView.tsx'),
        loader: chatbotViewLoader,
      },
    ],
  },
];

export const browserRouter = createBrowserRouter(routes);
